// Pure helpers used across the Inventory page and its dialogs.
// Extracted from InventoryPage.jsx — no React state inside, no side effects.
// Refactor goal: reduce duplication, improve testability and reuse.

/**
 * Sum the 3 zone quantities (Reserve + Stand + Siege) into the total quantity.
 * Each value can be a string or a number; any invalid value is treated as 0.
 */
export function calculateZoneTotal(reserve, stand, siege) {
    return (parseFloat(reserve) || 0) + (parseFloat(stand) || 0) + (parseFloat(siege) || 0);
}

/**
 * Calculate the line total (€) for a form-item before save.
 * - If quantity is in 'purchase' units → price is per purchase unit, multiply directly.
 * - If quantity is in 'base' units (kg/L/piece) → convert to purchase units via capacity_value
 *   when available, otherwise fall back to base*price (legacy behavior).
 */
export function calculateLineTotal(item) {
    if (!item.unit_price || !item.quantity) return 0;
    if (item.input_unit === 'purchase') {
        return item.quantity * item.unit_price;
    }
    if (item.capacity_value && item.capacity_value > 0) {
        const purchaseUnits = item.quantity / item.capacity_value;
        return purchaseUnits * item.unit_price;
    }
    return item.quantity * item.unit_price;
}

/**
 * Effective unit price to persist. Always the supplier unit price (per purchase unit).
 * Kept as a function for API stability and future overrides.
 */
export function calculateEffectiveUnitPrice(item) {
    return item.unit_price || 0;
}

/**
 * Convert a form-item's quantity to the canonical base-unit value persisted in DB.
 * When the user typed in 'purchase' units, multiply by capacity_value.
 */
export function calculateEffectiveQuantity(item) {
    if (item.input_unit === 'purchase' && item.capacity_value && item.capacity_value > 0) {
        return item.quantity * item.capacity_value;
    }
    return item.quantity;
}

/**
 * Convert the per-zone form quantities (reserve/stand/siege) to canonical base-unit values.
 * Same conversion rule as `calculateEffectiveQuantity`, applied to each zone independently.
 */
export function calculateEffectiveZoneQuantities(item) {
    const multiplier = (item.input_unit === 'purchase' && item.capacity_value && item.capacity_value > 0)
        ? item.capacity_value
        : 1;
    return {
        quantity_reserve: (item.quantity_reserve || 0) * multiplier,
        quantity_stand: (item.quantity_stand || 0) * multiplier,
        quantity_siege: (item.quantity_siege || 0) * multiplier,
    };
}

/**
 * Group an inventory group's items by product category (resolved via products lookup).
 * Items inside each category are sorted by product name. Categories are returned in alphabetical order
 * thanks to the upstream sort + insertion-ordered keys.
 *
 * Returns: { [category: string]: Inventory[] }
 */
export function getGroupedInventoryData(selectedInventoryGroup, products) {
    if (!selectedInventoryGroup) return {};
    const productCategoryOf = (productId) => {
        const p = products.find(pr => pr.id === productId);
        return p?.category || '';
    };
    const sortedItems = [...selectedInventoryGroup.items].sort((a, b) => {
        const catA = productCategoryOf(a.industrial_product_id) || '';
        const catB = productCategoryOf(b.industrial_product_id) || '';
        if (catA !== catB) return catA.localeCompare(catB);
        return (a.product_name || '').localeCompare(b.product_name || '');
    });
    const groupedByCategory = {};
    sortedItems.forEach(inv => {
        const category = productCategoryOf(inv.industrial_product_id) || 'Sans catégorie';
        if (!groupedByCategory[category]) groupedByCategory[category] = [];
        groupedByCategory[category].push(inv);
    });
    return groupedByCategory;
}

/**
 * Resolve the two unit labels displayed in the inventory line toggle.
 *
 * Two shapes exist in the data:
 *  1) Simple products: `capacity_value` is expressed in the product base unit
 *     (e.g., a Bidon of 5L → capacity_value=5, capacity_unit='L', product.unit='L'),
 *     or a Paquet contains N pieces used individually (e.g., Lavette x25 → billing='Paquet',
 *     invoice='Paquet', order='carton', capacity_value=25 pieces per Paquet).
 *     → purchase label = billing_unit ("Bidon"/"Paquet"), base label = product.unit ("L"/"pièce").
 *  2) Pack products: `capacity_unit === 'pièce'` AND `billing_unit !== order_unit` AND
 *     `invoice_unit === order_unit` (invoicing happens in the OUTER packaging).
 *     Here `capacity_value` counts how many billing units fit in one order unit
 *     (e.g., a Carton contains 4 Poches → capacity_value=4, invoice_unit=Carton).
 *     → purchase label = order_unit ("Carton"), base label = billing_unit ("Poche").
 *     The DB `quantity` is the count of billing units (Poches).
 *
 * The `invoice_unit === order_unit` gate discriminates true packs (Suma Bac Divermite:
 * invoice=Carton) from products whose billing unit is a self-contained pack usable by
 * the piece (Lavette: invoice=Paquet even though ordered in cartons of 25 lavettes).
 */
export function resolveInventoryUnits(sp, product) {
    const billingUnit = sp?.billing_unit || '';
    const orderUnit = sp?.order_unit || '';
    const invoiceUnit = sp?.invoice_unit || '';
    const capacityUnit = sp?.capacity_unit || '';
    const isPackProduct = capacityUnit === 'pièce'
        && billingUnit
        && orderUnit
        && billingUnit.toLowerCase() !== orderUnit.toLowerCase()
        && invoiceUnit
        && invoiceUnit.toLowerCase() === orderUnit.toLowerCase()
        && (sp?.capacity_value || 0) > 0;
    if (isPackProduct) {
        return { purchaseUnitLabel: orderUnit, baseUnit: billingUnit };
    }
    const purchaseUnitLabel = billingUnit || invoiceUnit || orderUnit || 'unité';
    const baseUnit = product?.unit || 'pièce';
    return { purchaseUnitLabel, baseUnit };
}

/**
 * Compute display-time data for a persisted inventory line, given the current product/supplier_product
 * lookup tables. Used by both the desktop table and the mobile cards.
 *
 * Returns:
 *   { sp, product, baseUnit, purchaseUnits, lineTotal, qtyReserve, qtyStand, qtySiege,
 *     capacityValue, purchaseUnitLabel }
 */
export function getInvItemData(inv, supplierProducts, products) {
    const sp = supplierProducts.find(s => s.id === inv.supplier_product_id);
    const product = products.find(p => p.id === inv.industrial_product_id);
    const capacityValue = sp?.capacity_value || 1;
    const purchaseUnits = capacityValue > 0 ? (inv.quantity || 0) / capacityValue : (inv.quantity || 0);
    const lineTotal = purchaseUnits * (inv.unit_price || 0);
    const qtyReserve = inv.quantity_reserve || 0;
    const qtyStand = inv.quantity_stand !== undefined ? inv.quantity_stand : inv.quantity;
    const qtySiege = inv.quantity_siege || 0;
    const { purchaseUnitLabel, baseUnit } = resolveInventoryUnits(sp, product);
    return {
        sp,
        product,
        baseUnit,
        purchaseUnits,
        lineTotal,
        qtyReserve,
        qtyStand,
        qtySiege,
        capacityValue,
        purchaseUnitLabel,
    };
}
